import { Controller } from "@hotwired/stimulus"

// Demo-only: powers the gated Surveys mock.
//  • The "Next required question" pill scrolls to the first required question
//    that still has no answer, and hides once all required questions are done.
//  • runDemo() (fired by typewriter:done, once the "Where did you stay" answer
//    finishes typing) plays a short scripted walkthrough: pick a "How long"
//    option, scroll to + check "Social Security", fake-tap "Next required
//    question", answer the health-insurance question, then scroll to the
//    insurance-name field (which types itself in via its own typewriter).
export default class extends Controller {
  static targets = [
    "question",
    "button",
    "howLong",
    "socialSecurity",
    "coveredYes",
    "insuranceName",
  ]

  timers = []
  demoRan = false

  connect() {
    this.update()
  }

  disconnect() {
    this.timers.forEach(clearTimeout)
  }

  runDemo() {
    if (this.demoRan) return
    this.demoRan = true

    this.after(700, () => this.tapOption(this.howLongTarget))
    this.after(1700, () => this.scrollToQuestion(this.socialSecurityTarget))
    this.after(2700, () => this.tapOption(this.socialSecurityTarget))
    this.after(3700, () => this.tap(this.buttonTarget))
    this.after(3950, () => this.next()) // → scrolls to "covered"
    this.after(5100, () => this.tapOption(this.coveredYesTarget))
    this.after(6200, () =>
      this.insuranceNameTarget?.scrollIntoView({ behavior: "smooth", block: "center" }),
    )
    // Start the insurance-name typewriter only after the scroll settles (it has
    // autostart off, so it won't fire early while it's already on screen).
    this.after(7000, () => this.startTypewriter(this.insuranceNameTarget))
  }

  startTypewriter(el) {
    this.application
      .getControllerForElementAndIdentifier(el, "typewriter")
      ?.start()
  }

  next() {
    const target =
      this.questionTargets.find((q) => !this.answered(q)) ||
      this.questionTargets[this.questionTargets.length - 1]
    target?.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // Re-evaluated on every input/change (and after the typewriter types).
  update() {
    if (!this.hasButtonTarget) return
    const done = this.questionTargets.every((q) => this.answered(q))
    this.buttonTarget.classList.toggle("hidden", done)
  }

  answered(question) {
    return Array.from(question.querySelectorAll("input, textarea")).some((el) => {
      if (el.type === "radio" || el.type === "checkbox") return el.checked
      return el.value.trim() !== ""
    })
  }

  // ── demo helpers ──────────────────────────────────────────────────────────
  after(ms, fn) {
    this.timers.push(setTimeout(fn, ms))
  }

  scrollToQuestion(el) {
    el?.closest('[data-survey-mock-target="question"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // Ripple-tap an option's card (a teal wash that sweeps across it), then select
  // it just after — so it reads as a real finger press.
  tapOption(input) {
    if (!input) return
    this.pressAndRipple(input.closest("label") || input, "mock-tap-fill")
    setTimeout(() => input.click(), 130)
  }

  // Faux finger-tap on a button — the dashboard mock's ring ripple.
  tap(el) {
    this.pressAndRipple(el, "mock-tap-ripple")
  }

  pressAndRipple(el, rippleClass) {
    if (!el) return
    el.classList.add("mock-tap")
    setTimeout(() => el.classList.remove("mock-tap"), 350)

    const ripple = document.createElement("span")
    ripple.className = rippleClass
    el.appendChild(ripple)
    setTimeout(() => ripple.remove(), 600)
  }
}
